import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Plus, MessageCircle, UserPlus, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function Groups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allGroups, setAllGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    category: '',
    is_public: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllGroups();
    fetchMyGroups();
  }, []);

  const fetchAllGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups?limit=50`);
      setAllGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchMyGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups/my`, { withCredentials: true });
      setMyGroups(response.data);
    } catch (error) {
      console.error('Error fetching my groups:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim() || !newGroup.description.trim() || !newGroup.category) {
      toast.error(t('groups.fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/groups`, newGroup, { withCredentials: true });
      toast.success(t('groups.groupCreated'));
      setShowCreateDialog(false);
      setNewGroup({ name: '', description: '', category: '', is_public: true });
      fetchAllGroups();
      fetchMyGroups();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join`, {}, { withCredentials: true });
      toast.success(t('groups.joinedGroup'));
      fetchAllGroups();
      fetchMyGroups();
    } catch (error) {
      console.error('Error joining group:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`, {}, { withCredentials: true });
      toast.success(t('groups.leftGroup'));
      fetchAllGroups();
      fetchMyGroups();
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    }
  };

  const categories = [
    { value: 'design', label: t('groups.categories.design') },
    { value: 'coding', label: t('groups.categories.coding') },
    { value: 'language', label: t('groups.categories.language') },
    { value: 'cooking', label: t('groups.categories.cooking') },
    { value: 'fitness', label: t('groups.categories.fitness') },
    { value: 'music', label: t('groups.categories.music') },
    { value: 'business', label: t('groups.categories.business') },
    { value: 'other', label: t('groups.categories.other') }
  ];

  const GroupCard = ({ group, isMember }) => (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{group.name}</h3>
            <Badge variant="secondary" className="bg-teal-50 text-teal-600">
              {categories.find(c => c.value === group.category)?.label || group.category}
            </Badge>
          </div>
          <p className="text-gray-600 text-sm mb-3">{group.description}</p>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Users className="h-4 w-4" />
            <span>{group.member_count} {t('groups.members')}</span>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        <Button
          onClick={() => navigate(`/groups/${group.id}`)}
          variant="outline"
          size="sm"
          className="flex-1 border-teal-500 text-teal-600 hover:bg-teal-50"
        >
          <MessageCircle className="mr-2 h-4 w-4" />
          {t('groups.viewChat')}
        </Button>
        
        {isMember ? (
          <Button
            onClick={() => handleLeaveGroup(group.id)}
            variant="outline"
            size="sm"
            className="border-red-400 text-red-500 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={() => handleJoinGroup(group.id)}
            size="sm"
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t('groups.join')}
          </Button>
        )}
      </div>
    </Card>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-teal-500" />
            <h1 className="text-3xl font-bold text-gray-900">{t('groups.title')}</h1>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('groups.createGroup')}
          </Button>
        </div>

        <Tabs defaultValue="all">
          <TabsList>
            <TabsTrigger value="all">{t('groups.allGroups')}</TabsTrigger>
            <TabsTrigger value="my">{t('groups.myGroups')}</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allGroups.map(group => (
                <GroupCard
                  key={group.id}
                  group={group}
                  isMember={myGroups.some(g => g.id === group.id)}
                />
              ))}
            </div>
            {allGroups.length === 0 && (
              <p className="text-center text-gray-500 py-12">{t('groups.noGroups')}</p>
            )}
          </TabsContent>

          <TabsContent value="my" className="space-y-4 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myGroups.map(group => (
                <GroupCard key={group.id} group={group} isMember={true} />
              ))}
            </div>
            {myGroups.length === 0 && (
              <p className="text-center text-gray-500 py-12">{t('groups.noGroupsJoined')}</p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('groups.createGroup')}</DialogTitle>
            <DialogDescription>{t('groups.createDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.groupName')}</label>
              <Input
                placeholder={t('groups.namePlaceholder')}
                value={newGroup.name}
                onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                maxLength={50}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.category')}</label>
              <Select value={newGroup.category} onValueChange={(val) => setNewGroup({...newGroup, category: val})}>
                <SelectTrigger>
                  <SelectValue placeholder={t('groups.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('groups.description')}</label>
              <Textarea
                rows={3}
                placeholder={t('groups.descriptionPlaceholder')}
                value={newGroup.description}
                onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateGroup} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
              {loading ? t('common.processing') : t('groups.createGroup')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
